import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { clearCart } from "../cart/cart-utils";

/**
 * Página de Sucesso pós-pagamento
 * - Limpa carrinho 1x por encomenda
 * - Busca links de download por referência
 * - Gera/abre recibo
 * - Envia automaticamente (1x) por e-mail: recibo + link de download
 * - Botões para reenvio manual
 */

const API =
  process.env.REACT_APP_API ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.lucianacosta.cv";

export default function CheckoutSuccess() {
  const location = useLocation();
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Parâmetros vindos do retorno
  const status = qs.get("status"); // "ok" | "ko"
  const provider = qs.get("provider"); // "sisp" | "stripe" | ...
  const orderId = qs.get("orderId") || qs.get("orderref") || qs.get("orderrefid") || "";
  const orderRef = qs.get("ref") || orderId;
  const amountCVE = qs.get("amountCVE") || ""; // inteiro (CVE)
  const currency = qs.get("ccy") || qs.get("currency") || "CVE";
  const customerEmailQS = qs.get("email") || "";

  // Estado UI
  const [hasCleared, setHasCleared] = useState(false);

  const [downloads, setDownloads] = useState([]); // [{ name, url, expiresAt }]
  const [dlLoading, setDlLoading] = useState(false);
  const [dlError, setDlError] = useState("");

  const [receiptUrl, setReceiptUrl] = useState("");
  const [rcpLoading, setRcpLoading] = useState(false);
  const [rcpError, setRcpError] = useState("");

  const [sendToEmail, setSendToEmail] = useState(customerEmailQS);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendMsg, setSendMsg] = useState("");

  const [sendDLLoading, setSendDLLoading] = useState(false);
  const [sendDLMsg, setSendDLMsg] = useState("");

  const abortRef = useRef(null);

  // util
  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copiado para a área de transferência.");
    } catch { }
  };

  const humanAmount = () => {
    if (!amountCVE) return null;
    try {
      const v = toNumber(amountCVE);
      const eur = v / 110.265; // 1 € ≈ 110.265 CVE

      // Formatar com ponto como separador de milhar
      const formattedCVE = v.toLocaleString("pt-PT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).replace(/\./g, "."); // garante que usa ponto, não espaço

      return `${formattedCVE} CVE (${eur.toFixed(2)} €)`;
    } catch {
      return `${amountCVE} CVE`;
    }
  };


  // 1) Limpar carrinho UMA vez por encomenda
  useEffect(() => {
    if (!orderRef) return;
    const key = `checkout:cleared:${orderRef}`;
    const already = sessionStorage.getItem(key);
    if (!already) {
      try {
        clearCart();
      } catch { }
      sessionStorage.setItem(key, "1");
      setHasCleared(true);
    }
  }, [orderRef]);

  // 2) Buscar links de download
  useEffect(() => {
    if (!orderRef) return;
    setDlLoading(true);
    setDlError("");

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    fetch(`${API}/api/download/by-ref/${encodeURIComponent(orderRef)}`, {
      method: "GET",
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        const arr = Array.isArray(json?.downloads)
          ? json.downloads
          : json?.url
            ? [{ name: "Download", url: json.url, expiresAt: null }]
            : [];
        setDownloads(arr);
      })
      .catch((err) => setDlError(err.message || "Falha a obter downloads"))
      .finally(() => setDlLoading(false));

    return () => ctrl.abort();
  }, [orderRef]);

  // 2.1) Tentar preencher e-mail a partir dos dados da encomenda (fallback)
  useEffect(() => {
    if (sendToEmail || !orderId) return;
    let abort = new AbortController();
    (async () => {
      try {
        const r = await fetch(`${API}/api/orders/${encodeURIComponent(orderId)}`, {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: abort.signal,
        });
        if (!r.ok) return;
        const j = await r.json();
        const mail = j?.customer?.email || j?.email;
        if (mail) setSendToEmail(mail);
      } catch { }
    })();
    return () => abort.abort();
  }, [orderId, sendToEmail]);

  // 3) Gerar/obter Recibo
  const fetchReceipt = useCallback(async () => {
    if (!orderId) return;
    setRcpLoading(true);
    setRcpError("");
    try {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const r = await fetch(`${API}/api/orders/${encodeURIComponent(orderId)}/receipt`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
        signal: ctrl.signal,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      if (json?.pdfUrl) setReceiptUrl(json.pdfUrl);
      else throw new Error("Resposta sem pdfUrl");
    } catch (e) {
      setRcpError(e.message || "Falha a obter recibo");
    } finally {
      setRcpLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (status === "ok" && orderId) fetchReceipt();
  }, [status, orderId, fetchReceipt]);

  // 4A) Reenviar RECIBO por e-mail (manual)
  const resendReceipt = useCallback(async () => {
    if (!orderId) return;
    setSendLoading(true);
    setSendMsg("");
    try {
      const amountNumber = toNumber(amountCVE);

      const r = await fetch(`${API}/api/recibos/enviar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({
          orderId,
          email: sendToEmail || undefined,
          name: "",
          amount: amountNumber,
          currency: currency || "CVE",
          method: (provider || "SISP").toUpperCase(),
          productDescription: downloads?.[0]?.name || "Livro digital (PDF)",
          items: [
            {
              description: downloads?.[0]?.name || "Livro digital (PDF)",
              qty: 1,
              unitPriceCVE: amountNumber,
            },
          ],
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setSendMsg(
        json?.ok
          ? "✅ Recibo enviado por e-mail."
          : "⚠️ Pedido enviado, verifique a sua caixa de entrada."
      );
    } catch (e) {
      setSendMsg(`❌ Erro ao enviar recibo: ${e.message || "tente novamente"}`);
    } finally {
      setSendLoading(false);
    }
  }, [orderId, sendToEmail, amountCVE, currency, provider, downloads]);

  // 4B) Enviar LINK DE DOWNLOAD por e-mail (manual)
  const sendDownloadLink = useCallback(
    async (file) => {
      if (!orderId || !downloads?.length) return;
      setSendDLLoading(true);
      setSendDLMsg("");
      try {
        const d = file || downloads[0];
        const body = {
          email: sendToEmail || customerEmailQS || undefined,
          orderId,
          amount: toNumber(amountCVE),
          filename: d?.name || "ebook.pdf",
          url: d?.url,
        };
        const r = await fetch(
          `${API}/api/orders/${encodeURIComponent(orderId)}/send-download`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            credentials: "include",
            body: JSON.stringify(body),
          }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        setSendDLMsg(
          json?.ok
            ? "✅ Link de download enviado por e-mail."
            : "⚠️ Não foi possível confirmar o envio."
        );
      } catch (e) {
        setSendDLMsg(`❌ Erro ao enviar link: ${e.message || "tente novamente"}`);
      } finally {
        setSendDLLoading(false);
      }
    },
    [orderId, downloads, sendToEmail, customerEmailQS, amountCVE]
  );

  // 5) AUTOMÁTICO: enviar 1x recibo + link de download quando houver downloads
  // const autoSentRef = useRef(false);
  // useEffect(() => {
  //   if (status !== "ok" || !orderRef || !orderId) return;
  //   if (!downloads?.length) return;

  //   const autoKey = `checkout:emailed:${orderRef}`;
  //   if (sessionStorage.getItem(autoKey) || autoSentRef.current) return;

  //   (async () => {
  //     try {
  //       // await resendReceipt();
  //       // await sendDownloadLink();
  //       sessionStorage.setItem(autoKey, "1");
  //       autoSentRef.current = true;
  //     } catch {
  //       // evita crash; os botões continuam disponíveis
  //     }
  //   })();
  // }, [downloads, status, orderId, orderRef, resendReceipt]);

  // Componentes auxiliares
  const SuccessBadge = () => (
    <div className="alert alert-success d-flex align-items-center" role="alert">
      <span className="me-2">✅</span>
      <div>
        Pagamento confirmado{provider ? ` via ${provider.toUpperCase()}` : ""}.
        {orderRef ? (
          <>
            <br /> Referência da encomenda: <strong>{orderRef}</strong>
          </>
        ) : null}
        {amountCVE ? (
          <>
            <br /> Valor: <strong>{humanAmount()}</strong>
          </>
        ) : null}
      </div>
    </div>
  );

  const ErrorBadge = () => (
    <div className="alert alert-danger d-flex align-items-center" role="alert">
      <span className="me-2">❌</span>
      <div>
        O pagamento não foi concluído. {provider ? `(${provider})` : ""}
        <br /> Se isto for um engano, verifique o seu e-mail ou contacte o suporte.
      </div>
    </div>
  );

  const isOk = status === "ok";

  return (
    <div className="container py-5" style={{ maxWidth: 860 }}>
      <header className="mb-4 text-center">
        <h1 className="h3 mb-2">
          {isOk ? "Obrigado pela sua compra!" : "Estado do pagamento"}
        </h1>
        <p className="text-muted mb-0">Esta é a página de confirmação do seu pedido.</p>
      </header>

      {/* {isOk ? <SuccessBadge /> : <ErrorBadge />}
      {isOk && (
        <p className="text-success small mt-2">
          📧 O link para download do livro e o recibo em PDF já foram enviados para o seu e-mail.
          Se não receber em alguns minutos, verifique também a pasta de spam/lixo.
        </p>
      )} */}

      {isOk && (
        <p className="text-success small mt-2">
          📧 O recibo da sua compra (com a fatura em PDF em anexo) já foi enviado para o seu e-mail.
          Se não receber em alguns minutos, verifique também a pasta de spam/lixo.
          {/* O link para download está disponível acima e pode ser reenviado por e-mail sempre que precisar. */}
          <br />
          {/* Se não receber em alguns minutos, verifique também a pasta de spam/lixo. */}
        </p>
      )}

      {/* Downloads */}
      <section className="mb-5">
        <div className="d-flex align-items-center mb-2">
          <h2 className="h5 mb-0">Os seus downloads</h2>
          {dlLoading && <span className="ms-2 small text-muted">(a carregar...)</span>}
        </div>

        {dlError ? (
          <div className="alert alert-warning">
            Não foi possível obter os downloads. Por favor, tente novamente mais tarde ou verifique o seu e-mail 📩 {dlError}
          </div>
        ) : downloads?.length ? (
          <div className="list-group">
            {downloads.map((d, idx) => (
              <div
                key={idx}
                className="list-group-item d-flex justify-content-between align-items-center"
              >
                <div>
                  <div className="fw-semibold">{d.name || `Ficheiro ${idx + 1}`}</div>
                  {d.expiresAt && (
                    <div className="small text-muted">
                      Expira em {new Date(d.expiresAt).toLocaleString()}
                    </div>
                  )}
                  {d.url && (
                    <button
                      className="btn btn-link p-0 small"
                      onClick={() => copy(d.url)}
                      type="button"
                      title="Copiar link"
                    >
                      Copiar link
                    </button>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <a
                    href={d.url}
                    className="btn btn-primary btn-sm"
                    target="_blank"
                    rel="noreferrer"
                    aria-disabled={!isOk || !d.url}
                  >
                    Baixar
                  </a>
                  <button
                    className="btn btn-outline-success btn-sm"
                    onClick={() => sendDownloadLink(d)}
                    disabled={!isOk || !orderId || sendDLLoading}
                  >
                    {sendDLLoading ? "A enviar..." : "Enviar por e-mail"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">Nenhum download disponível ainda.</p>
        )}
        {sendDLMsg && <div className="small mt-2">{sendDLMsg}</div>}
      </section>

      {/* Recibo */}
      <section className="mb-5">
        <div className="d-flex align-items-center mb-2">
          <h2 className="h5 mb-0">Recibo</h2>
          {rcpLoading && <span className="ms-2 small text-muted">(a gerar...)</span>}
        </div>
        {rcpError && <div className="alert alert-warning">{rcpError}</div>}
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={fetchReceipt}
            disabled={!isOk || !orderId || rcpLoading}
          >
            {receiptUrl ? "Regerar recibo" : "Gerar recibo"}
          </button>
          {receiptUrl && (
            <>
              <a
                className="btn btn-primary btn-sm"
                href={receiptUrl}
                target="_blank"
                rel="noreferrer"
              >
                Abrir Recibo
              </a>
              <button className="btn btn-link btn-sm" onClick={() => copy(receiptUrl)}>
                Copiar URL
              </button>
            </>
          )}
        </div>
      </section>

      {/* Enviar por e-mail */}
      <section className="mb-5">
        <h2 className="h5">Enviar por e-mail</h2>
        <div className="row g-2 align-items-center">
          <div className="col-12 col-md-6">
            <input
              type="email"
              className="form-control"
              placeholder="o.seu@email.com"
              value={sendToEmail}
              onChange={(e) => setSendToEmail(e.target.value)}
            />
          </div>
          <div className="col-auto">
            <button
              className="btn btn-success"
              onClick={resendReceipt}
              disabled={!isOk || !orderId || sendLoading}
            >
              {sendLoading ? "A enviar..." : "Enviar Recibo"}
            </button>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-outline-success"
              onClick={() => sendDownloadLink()}
              disabled={!isOk || !orderId || sendDLLoading || !downloads?.length}
            >
              {sendDLLoading ? "A enviar..." : "Enviar Link de Download"}
            </button>
          </div>
        </div>
        {sendMsg && <div className="small mt-2">{sendMsg}</div>}
      </section>

      <hr />

      <footer className="d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          {hasCleared ? "(Carrinho limpo)" : "(Carrinho por limpar)"}
        </div>
        <div className="d-flex gap-2">
          <Link to="/" className="btn btn-outline-secondary">
            Voltar à loja
          </Link>
          <Link to="/conta/encomendas" className="btn btn-outline-secondary">
            Ver encomendas
          </Link>
        </div>
      </footer>
    </div>
  );
}
